import React, { useState, useMemo } from 'react';
import { cn } from '../../lib/utils';
import FinanceMockup from '../../assets/finance.png';
import InventoryMockup from '../../assets/inventory.png';
import SalesMarketingMockup from '../../assets/sales_and_marketing.png';
import Analytics from '../../lib/analytics';
import LogoLoop from '../../components/LogoLoop';

// Integration Logos
import QuickBooksLogo from '../../assets/integrations/finance-avl-quickbooks.png';
import ZohoBooksLogo from '../../assets/integrations/finance-avl-zoho_books.png';
import StripeLogo from '../../assets/integrations/finance-soon-stripe.png';
import ZohoCRMLogo from '../../assets/integrations/snm-avl-Zoho_CRM.png';
import SalesforceLogo from '../../assets/integrations/snm-avl-salesforce.png';
import HubSpotLogo from '../../assets/integrations/snm-soon-hubspot.png';
import PipedriveLogo from '../../assets/integrations/snm-soon-pipedrive.png';
import ShopifyLogo from '../../assets/integrations/ops-avl-Shopify.png';
import Cin7Logo from '../../assets/integrations/ops-soon-Cin7.png';
import NetSuiteLogo from '../../assets/integrations/ops-soon-Netsuite.png';

// Temporarily commented out - may be needed for future mobile implementation
/* const useCasesData = {
  Finance: {
    heading: 'Smart Financial Decisions',
    subheading: 'Analyze purchase ROI and budget allocation in real-time',
    phoneContent: {
      title: 'Purchase Decision Analysis',
      metrics: [
        {
          label: 'ROI Forecast',
          value: '247%',
          trend: '+12%',
          color: 'text-green-600',
        },
        {
          label: 'Budget Allocation',
          value: '$2.4M',
          trend: 'optimal',
          color: 'text-blue-600',
        },
        {
          label: 'Break-even Timeline',
          value: '8.2 months',
          trend: '-0.8',
          color: 'text-green-600',
        },
        { label: 'Risk Assessment', value: 'Low', color: 'text-green-600' },
      ],
      message:
        'Recommended: Proceed with Q4 expansion. ROI projections exceed target by 47%.',
    },
  },
  'Sales & Marketing': {
    heading: 'Lead Generation Insights',
    subheading: 'Discover why referrals outperform paid channels by 3x',
    phoneContent: {
      title: 'Channel Performance Dashboard',
      metrics: [
        {
          label: 'Referrals',
          value: '3.2x ROI',
          trend: '+45%',
          color: 'text-green-600',
        },
        {
          label: 'Paid Social',
          value: '1.1x ROI',
          trend: '-8%',
          color: 'text-red-500',
        },
        {
          label: 'CAC (Referral)',
          value: '$67',
          trend: '-23%',
          color: 'text-green-600',
        },
        {
          label: 'CAC (Paid)',
          value: '$184',
          trend: '+12%',
          color: 'text-red-500',
        },
      ],
      highlights: [
        'Shift 40% budget from paid to referral programs',
        'Focus on customer satisfaction for organic growth',
      ],
    },
  },
  Sales: {
    heading: 'Product Performance Tracking',
    subheading: 'Monitor your newest products and sales trends instantly',
    phoneContent: {
      title: 'Product Insights Dashboard',
      metrics: [
        {
          label: 'New Product Revenue',
          value: '$1.8M',
          trend: '+167%',
          color: 'text-green-600',
        },
        {
          label: 'Market Penetration',
          value: '23%',
          trend: '+8%',
          color: 'text-green-600',
        },
        {
          label: 'Customer Adoption',
          value: '2,847',
          trend: '+89%',
          color: 'text-green-600',
        },
        {
          label: 'Conversion Rate',
          value: '12.4%',
          trend: '+3.2%',
          color: 'text-green-600',
        },
      ],
      message:
        'New product line exceeding projections. Ready for market expansion.',
    },
  },
  Operations: {
    heading: 'Inventory Optimization',
    subheading:
      'Identify that 100 products drive 80% of revenue, optimize the rest',
    phoneContent: {
      title: 'Inventory Intelligence',
      metrics: [
        {
          label: 'Revenue Concentration',
          value: '80%',
          trend: 'from 100 SKUs',
          color: 'text-blue-600',
        },
        {
          label: 'Idle Inventory',
          value: '200 SKUs',
          trend: '12 months+',
          color: 'text-red-500',
        },
        {
          label: 'Turnover Rate',
          value: '4.2x',
          trend: '+0.8x',
          color: 'text-green-600',
        },
        {
          label: 'Storage Cost Savings',
          value: '$340K',
          trend: 'projected',
          color: 'text-green-600',
        },
      ],
      highlights: [
        'Remove 200 underperforming SKUs',
        'Focus on high-velocity products',
        'Reduce storage costs by 23%',
      ],
    },
  },
}; */

// Integration data structure
interface Integration {
  name: string;
  logo: string;
}

interface IntegrationSet {
  available: Integration[];
  comingSoon: Integration[];
}

interface IntegrationsData {
  [key: string]: IntegrationSet;
}

const integrationsData: IntegrationsData = {
  Finance: {
    available: [
      { name: 'QuickBooks', logo: QuickBooksLogo },
      { name: 'Zoho Books', logo: ZohoBooksLogo },
    ],
    comingSoon: [{ name: 'Stripe', logo: StripeLogo }],
  },
  'Sales & Marketing': {
    available: [
      { name: 'Zoho CRM', logo: ZohoCRMLogo },
      { name: 'Salesforce', logo: SalesforceLogo },
    ],
    comingSoon: [
      { name: 'HubSpot', logo: HubSpotLogo },
      { name: 'Pipedrive', logo: PipedriveLogo },
    ],
  },
  Operations: {
    available: [{ name: 'Shopify', logo: ShopifyLogo }],
    comingSoon: [
      { name: 'Cin7', logo: Cin7Logo },
      { name: 'NetSuite', logo: NetSuiteLogo },
    ],
  },
};

const tabs = ['Finance', 'Sales & Marketing', 'Operations'];

// Mobile logo rendering logic
const createMobileLogoData = (logos: { name: string; logo: string }[]) => {
  return logos.map(integration => ({
    src: integration.logo,
    alt: integration.name,
    title: integration.name,
  }));
};

const AppMockup: React.FC<{ imageSrc: string }> = ({ imageSrc }) => {
  return (
    <div className="relative mx-auto w-[350px] lg:w-[400px] xl:w-[450px]">
      <div className="relative h-[480px] overflow-hidden lg:h-[540px] xl:h-[600px]">
        <img
          src={imageSrc}
          alt="App Mockup"
          className="absolute right-0 bottom-0 h-full w-auto object-contain object-bottom"
          style={{ maxHeight: '100%' }}
        />
      </div>
    </div>
  );
};

// Mobile-specific phone mockup component
const MobileAppMockup: React.FC<{ imageSrc: string }> = ({ imageSrc }) => {
  return (
    <div className="relative w-full px-2">
      <div className="relative h-[500px] overflow-hidden">
        <img
          src={imageSrc}
          alt="App Mockup"
          className="absolute right-0 bottom-0 h-full w-auto object-contain object-bottom"
          style={{ maxHeight: '100%' }}
        />
      </div>
    </div>
  );
};

interface UseCasesSectionProps {
  className?: string;
}

export const UseCasesSection: React.FC<UseCasesSectionProps> = ({
  className,
}) => {
  const [activeTab, setActiveTab] = useState('Finance');

  // Handle tab switching with analytics
  const handleTabSwitch = (newTab: string) => {
    if (newTab !== activeTab) {
      Analytics.trackUseCase('use_case_tab_switch', {
        from_tab: activeTab,
        to_tab: newTab,
        tab_name: newTab,
      });
      setActiveTab(newTab);
    }
  };

  // Mobile logos data
  const mobileAvailableLogos = useMemo(
    () => createMobileLogoData(integrationsData[activeTab]?.available || []),
    [activeTab]
  );

  const mobileComingSoonLogos = useMemo(
    () => createMobileLogoData(integrationsData[activeTab]?.comingSoon || []),
    [activeTab]
  );

  return (
    <section
      id="use-cases"
      className={cn(
        'relative overflow-hidden bg-violet-50 py-20 max-[599px]:py-0 lg:py-32',
        className
      )}
    >
      <div className="container mx-auto px-4 max-[599px]:pt-8 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div className="mb-16 text-center max-[599px]:mb-6">
          <h2 className="font-primary mb-6 text-4xl font-bold text-slate-900 max-[599px]:text-3xl lg:text-5xl">
            Where can DataWyse help me?
          </h2>
          <p className="mx-auto max-w-3xl text-xl text-slate-600 max-[599px]:hidden">
            See how DataWyse transforms workflows across every department
          </p>
        </div>

        {/* Desktop: Main Card Container - Expanded to Take Majority of Screen */}
        <div className="mx-auto max-w-[95vw] px-4 max-[599px]:hidden sm:px-6 lg:px-8">
          <div className="group relative">
            {/* Enhanced Card - Much Larger */}
            <div className="relative h-auto overflow-hidden rounded-3xl border border-slate-200/60 bg-white shadow-[0_16px_48px_rgba(0,0,0,0.12),0_8px_16px_rgba(0,0,0,0.08),0_4px_6px_rgba(0,0,0,0.04)] transition-all duration-500 hover:shadow-[0_24px_64px_rgba(0,0,0,0.16),0_12px_24px_rgba(0,0,0,0.12),0_6px_12px_rgba(0,0,0,0.08)]">
              {/* Subtle Purple Gradient Overlay - Bottom Left */}
              <div className="via-purple-25/20 pointer-events-none absolute bottom-0 left-0 h-1/3 w-1/3 bg-gradient-to-tr from-purple-50/40 to-transparent opacity-60"></div>
              {/* Tab Navigation with Mac Buttons - Larger */}
              <div className="relative z-10 border-b border-slate-200/60">
                <div className="flex h-20 w-full items-stretch">
                  {/* Mac-style buttons */}
                  <div className="flex items-center px-8">
                    <div className="flex items-center space-x-3">
                      <div className="h-4 w-4 cursor-pointer rounded-full bg-red-500 shadow-sm transition-colors hover:bg-red-600"></div>
                      <div className="h-4 w-4 cursor-pointer rounded-full bg-yellow-500 shadow-sm transition-colors hover:bg-yellow-600"></div>
                      <div className="h-4 w-4 cursor-pointer rounded-full bg-green-500 shadow-sm transition-colors hover:bg-green-600"></div>
                    </div>
                  </div>
                  <div className="flex flex-1">
                    {tabs.map(tab => (
                      <button
                        key={tab}
                        onClick={() => handleTabSwitch(tab)}
                        className={cn(
                          'flex h-full flex-1 items-center justify-center border-b-2 px-4 text-center text-sm font-medium whitespace-nowrap transition-all duration-200 lg:text-lg',
                          activeTab === tab
                            ? 'border-purple-600 bg-purple-50/50 text-purple-600'
                            : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
                        )}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Content Area - Larger Padding */}
              <div className="relative z-10 h-auto px-6 pt-6 pb-0 lg:px-8 lg:pt-8 lg:pb-0 xl:px-10 xl:pt-10 xl:pb-0">
                <div className="grid h-full grid-cols-1 gap-16 lg:grid-cols-2 xl:gap-20">
                  {/* Left Column - Integration Logos */}
                  <div className="flex h-full flex-col space-y-6 py-8">
                    {/* Integration Header */}
                    <div className="mb-6 px-2">
                      <h3 className="text-2xl font-bold tracking-wide text-black">
                        Integrates Seamlessly With Your Tools
                      </h3>
                    </div>

                    {/* Available Integrations */}
                    <div className="space-y-6">
                      {integrationsData[activeTab]?.available.length > 0 && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4 xl:grid-cols-3">
                            {integrationsData[activeTab].available.map(
                              (integration, index) => (
                                <div
                                  key={index}
                                  className="flex min-h-[80px] items-center justify-center rounded-lg border border-gray-100 bg-white p-4 shadow-md transition-shadow duration-200 hover:shadow-lg"
                                >
                                  <img
                                    src={integration.logo}
                                    alt={integration.name}
                                    className="h-auto max-h-12 w-full object-contain"
                                  />
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}

                      {/* Coming Soon Section */}
                      {integrationsData[activeTab]?.comingSoon.length > 0 && (
                        <div className="space-y-4">
                          <div className="px-2">
                            <h4 className="text-lg font-semibold text-gray-600">
                              Coming soon:
                            </h4>
                          </div>
                          <div className="grid grid-cols-2 gap-4 xl:grid-cols-3">
                            {integrationsData[activeTab].comingSoon.map(
                              (integration, index) => (
                                <div
                                  key={index}
                                  className="flex min-h-[80px] items-center justify-center rounded-lg border border-gray-100 bg-white p-4 opacity-70 shadow-md transition-shadow duration-200 hover:shadow-lg"
                                >
                                  <img
                                    src={integration.logo}
                                    alt={integration.name}
                                    className="h-auto max-h-12 w-full object-contain grayscale"
                                  />
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Column - App Mockup */}
                  <div className="flex items-center justify-center">
                    <AppMockup
                      imageSrc={
                        activeTab === 'Finance'
                          ? FinanceMockup
                          : activeTab === 'Sales & Marketing'
                            ? SalesMarketingMockup
                            : activeTab === 'Operations'
                              ? InventoryMockup
                              : FinanceMockup
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile: Integration Logos - Only visible on mobile <480px */}
        <div className="hidden max-[599px]:block mb-8">
          <div className="px-4 space-y-6">
            {/* Integration Header */}
            <div className="text-center">
              <h3 className="text-lg font-normal text-black">
                Integrates Seamlessly With:
              </h3>
            </div>

            {/* Available Logos */}
            {mobileAvailableLogos.length > 0 && (
              <div>
                {mobileAvailableLogos.length <= 2 ? (
                  // Static layout for 1-2 logos
                  <div className="flex justify-center items-center gap-6 py-3">
                    {mobileAvailableLogos.map((logo, index) => (
                      <div key={index} className="flex-shrink-0">
                        <img
                          src={logo.src}
                          alt={logo.alt}
                          title={logo.title}
                          className="h-10 w-auto object-contain"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  // LogoLoop for 3+ logos
                  <LogoLoop
                    logos={mobileAvailableLogos}
                    speed={60}
                    direction="left"
                    logoHeight={40}
                    gap={24}
                    pauseOnHover
                    fadeOut
                    fadeOutColor="#f8f9fa"
                    ariaLabel="Available integrations"
                    className="py-3"
                  />
                )}
              </div>
            )}

            {/* Coming Soon Logos */}
            {mobileComingSoonLogos.length > 0 && (
              <div>
                <div className="mb-3">
                  <h4 className="text-base font-normal text-gray-600 text-center">
                    Coming soon:
                  </h4>
                </div>
                {mobileComingSoonLogos.length <= 2 ? (
                  // Static layout for 1-2 logos
                  <div className="flex justify-center items-center gap-6 py-3">
                    {mobileComingSoonLogos.map((logo, index) => (
                      <div key={index} className="flex-shrink-0">
                        <img
                          src={logo.src}
                          alt={logo.alt}
                          title={logo.title}
                          className="h-10 w-auto object-contain"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  // LogoLoop for 3+ logos
                  <LogoLoop
                    logos={mobileComingSoonLogos}
                    speed={60}
                    direction="left"
                    logoHeight={40}
                    gap={24}
                    pauseOnHover
                    fadeOut
                    fadeOutColor="#f8f9fa"
                    ariaLabel="Coming soon integrations"
                    className="py-3"
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile: Pill Switchers + Bottom Phone Mockup */}
        <div className="hidden max-[599px]:block">
          <div className="flex flex-col">
            {/* Pill Switchers */}
            <div className="mb-4 flex justify-center gap-2 px-4">
              {tabs.map(tab => (
                <button
                  key={tab}
                  onClick={() => handleTabSwitch(tab)}
                  className={cn(
                    'flex-1 rounded-full px-3 py-2.5 text-xs font-medium whitespace-nowrap transition-all duration-200',
                    activeTab === tab
                      ? 'bg-purple-600 text-white shadow-lg'
                      : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  )}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Bottom-attached Phone Mockup */}
            <div className="flex items-end">
              <MobileAppMockup
                imageSrc={
                  activeTab === 'Finance'
                    ? FinanceMockup
                    : activeTab === 'Sales & Marketing'
                      ? SalesMarketingMockup
                      : activeTab === 'Operations'
                        ? InventoryMockup
                        : FinanceMockup
                }
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
